import React, { useMemo } from "react";
import { FieldProps, useEntityFetch } from "@firecms/core";
import { Select, SelectItem } from "@firecms/ui";
import { fieldCollection } from "../collections/field";

export function FieldSelect({
    property,
    value,
    setValue,
    customProps,
    context
}: FieldProps<string, { fieldId: string, dependsOn?: string, source?: "key_lookups" | "key_value" }>) {
    const { fieldId, dependsOn, source } = customProps;

    const filters = (context.values as any).filter_fields || {};
    const dependencyValue = dependsOn ? filters[dependsOn] : null;

    const { entity, dataLoading } = useEntityFetch({
        path: "field",
        entityId: fieldId,
        collection: fieldCollection,
        useCache: true
    });

    const options = useMemo(() => {
        const entityValues = (entity?.values as any) || {};
        const keyLookup = entityValues.key_lookups || entityValues.key_lookup || {};
        let opts: { id: string, label: string }[] = [];

        if (source === "key_value") {
            // curated {key, label} array on the field doc (e.g. industry)
            const keyValue = Array.isArray(entityValues.key_value) ? entityValues.key_value : [];
            opts = keyValue
                .filter((item: any) => item?.key && item?.label)
                .map((item: any) => ({ id: String(item.key), label: String(item.label) }));
        } else if (dependsOn) {
            // Find the right nested object. Try exact match, then case-insensitive.
            let nested = keyLookup[dependencyValue];
            if (!nested && typeof dependencyValue === "string") {
                const foundKey = Object.keys(keyLookup).find(k => k.toLowerCase() === dependencyValue.toLowerCase());
                if (foundKey) nested = keyLookup[foundKey];
            }

            if (nested && typeof nested === "object") {
                opts = Object.entries(nested).map(([k, v]) => ({ id: k, label: String(v) }));
            }
        } else {
            opts = Object.entries(keyLookup).map(([k, v]) => ({ id: k, label: String(v) }));
        }

        return opts.sort((a, b) => a.label.localeCompare(b.label));
    }, [entity, dependsOn, dependencyValue, source]);

    // Clear child value if parent changes and current value is no longer valid
    React.useEffect(() => {
        if (dependsOn && !dataLoading && options.length > 0 && value && value !== "none") {
            const isValid = options.some(opt => opt.id === value);
            if (!isValid) {
                setValue(null);
            }
        }
    }, [options, dataLoading, value, dependsOn, dependencyValue, setValue]);

    return (
        <Select
            value={value || "none"}
            onValueChange={(val) => {
                const newValue = val === "none" ? null : val;
                setValue(newValue);
            }}
            disabled={dataLoading || (!!dependsOn && !dependencyValue)}
            label={property.name}
            fullWidth
            position="popper"
        >
            <SelectItem value="none"><em>None</em></SelectItem>
            {options.map(opt => (
                <SelectItem key={opt.id} value={opt.id}>{opt.label}</SelectItem>
            ))}
        </Select>
    );
}
